/**
 * 작성자 : 홍선영
 * 날짜 : 2023.09.04
 * 경로 : 안전 관리/게시판 - 현장전달사항
 */

import { useState, useRef, useEffect, RefObject } from 'react';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useQuery } from '@tanstack/react-query';
import { PulseLoader } from 'react-spinners';
import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { IModal } from 'customTypes';
import { COMCD_USE_YN, INIT_USE_YN_A, INIT_USE_YN_Y } from '../../../_constants';
import { userState } from '../../../atoms';
import { SearchOptions } from '../../../assets/styles/SearchOptions';
import i18n from '../../../translation/i18n';
import { BtnGhost } from '../../../components/Button';
import RangePicker, { onChangeRangeInput } from '../../../components/RangePicker';
import { formatDateYMD, isValidYYYYMMDD } from '../../../utils/formatDate';
import Input from '../../../components/Input';
import Portal from '../../../components/Portal';
import TuiGrid from '../../../components/Table/TuiGrid';
import SelectBox from '../../../components/SelectBox';
import BackButton from '../../../components/BackButton';
import IssueGuide from '../../../components/IssueGuide';
import DeleteModal from '../../../components/Modal/DeleteModal2';
import TuiGridWrapper from '../../../components/Table/TuiGridWrapper';
import DashboardViewModal from '../../../components/Modal/DashboardViewModal';
import SiteTransferEditorInputForm from '../../../components/Form/SiteTransferEditorInputForm';
import { ynFilter } from '../../../utils/ynFilter';
import { trimObject } from '../../../utils/trimObject';
import { logPost } from '../../../services/log';
import { LoadingModalBackground } from '../../../assets/styles/Modal';
import { ContentsContainerRoot } from '../../../assets/styles/ContentsContainerRoot';
import { apiDelete, apiGet, apiPost } from '../../../services/_common';
import { useFetchCommonCodeList } from '../../../services/useSetCodeListInSelectBoxForm';
import useTuiGridSize from '../../../hooks/useTuiGridSize';
import CustomCircleChkRenderer from '../../../components/CustomCircleChkRenderer';
import { useDebounce } from '../../../utils/useDebounce';
import SearchFilterModal from '../../../components/Modal/SearchFilterModal';
import useBackHistory from '../../../hooks/useBackHistory';

const Root = styled(ContentsContainerRoot)`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  &.hideRoot {
    visibility: hidden;
    position: absolute;
  }
  .tui-container {
    flex-grow: 1;
    overflow: hidden;
    margin-left: 0.5rem;
  }
  > .inputFormsWrapper {
    height: 3.5rem !important;
    flex-shrink: 0;
    display: flex;
    flex-direction: row !important;
    align-items: center;
    padding: 0 1rem;
    border-bottom: 1px solid var(--outline);
  }
  .arrow-icon span {
    height: 2.5rem;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
  }
  .icon-btn {
    width: fit-content;
    display: flex;
    align-items: center;
    justify-content: center;
    user-select: none;
    gap: 0.25rem;
    cursor: pointer;
    border-radius: 5rem;
    padding-right: 1rem;
    padding-left: 0.5rem;
    font-size: 0.875rem;
    background-color: var(--tonal);
    color: var(--text-primary);
  }
  .icon-btn:hover {
    background-color: var(--tonal-deep);
  }
`;
interface Notice {
  cSeq: string;
  cTitle: string;
  cContent: string;
  bigo: string;
  useYn: string;
  cDate: string;
}

// type : 현장(s)/사내(c) 여부
const Transfer = ({ type }: { type: 's' | 'c' }) => {
  const { t } = useTranslation();
  const contentKey = type === 's' ? 'cContent' : 'cContents';
  const userInfo = useRecoilValue(userState);
  const [columns, setColumns] = useState<any[]>([]);
  const size = useOutletContext<any>();
  const userInfoInputFormRef = useRef<any>(null); // 인풋 폼 ref (테이블 로우를 클릭했을 때 바로 inputForm 으로 스크롤 이동시키기 위한 ref)
  const { data: useYnComCdListWithAll } = useFetchCommonCodeList(COMCD_USE_YN, true); // 사용여부 공통코드 목록 (전체포함)
  const [initNoticeList, setInitNoticeList] = useState<Notice[]>([]);
  const [noticeListForTui, setNoticeListForTui] = useState<Notice[] | null>(null);
  const [rowState, setRowState] = useState({
    cSeq: '',
    cTitle: '',
    [contentKey]: '',
    cDate: '',
    bigo: '',
    useYn: 'Y',
  }); // 서버에서 받아 온 raw data 어레이 중 1줄의 데이터 state
  const [isNewAdd, setIsNewAdd] = useState(true); // 신규등록 여부
  const [openModal, setOpenModal] = useState<IModal>({ status: false, type: '', title: '' });
  const [searchTitle, setSearchTitle] = useState({ title: '' });
  const [userInputDateRange, setUserInputDateRange] = useState({ start: '', end: '' });
  const [useYn, setUseYn] = useState(INIT_USE_YN_A);
  const [isSaveClicked, setIsSaveClicked] = useState(false); // 저장버튼 클릭 여부에 따라 필수입력값 보더색상 처리하기 위한 state 값
  const [selectedRowKey, setSelectedRowKey] = useState('');
  const [visibleRangePicker, setVisibleRangePicker] = useState(false);
  const [pickerDateRange, setPickerDateRange] = useState([{ startDate: '', endDate: '', key: 'selection' }]);
  const [editorData, setEditorData] = useState<string>('');
  const [cDate, setCDate] = useState('');
  const [date, setDate] = useState({ start: '', end: '' });
  const [openSubPage, setOpenSubPage] = useState(false);
  const [cSeq, setCSeq] = useState<null | string>(null);
  const [method, setMethod] = useState<'get' | 'delete'>('get');
  const componentRef = useRef<HTMLDivElement>(null);
  const { tuiHeight } = useTuiGridSize(componentRef);
  const reqInfo = type === 's' ? { path: '/safe/communicate', reqParam: { hCd: userInfo.hCd, sCd: userInfo.sCd } } : { path: '/safe/curgent', reqParam: { cCd: userInfo.hCd } };
  const debouncedTitle = useDebounce(searchTitle.title);

  useEffect(() => {
    logPost({
      hCd: userInfo.hCd,
      sCd: userInfo.sCd,
      userId: userInfo.userId,
      menu: `안전관리/게시판 > ${type === 's' ? '현장 전달사항' : '(사내) 긴급 공지사항'}`,
      action: '조회',
      etc: ``,
    });
  }, []);

  const prefixColumns = [
    {
      header: type === 's' ? t('게시 시작일자') : t('적용일자'),
      name: 'cDate',
      align: 'center',
      sortable: true,
      filter: 'select',
      width: 140,
      renderer: { classNames: ['text_secondary'] },
    },
  ];

  const suffixColumns = [
    {
      header: t('제목'),
      name: 'cTitle',
      align: 'left',
      sortable: true,
      minWidth: 200,
      renderer: { classNames: ['text_secondary'] },
    },
    {
      header: t(type === 's' ? `사용유무` : `상황판 적용`),
      name: 'useYn',
      align: 'center',
      renderer: {
        type: CustomCircleChkRenderer,
      },
      width: 100,
    },
  ];

  const expireColumns = [
    {
      header: t('게시 종료일자'),
      name: 'fDate',
      align: 'center',
      sortable: true,
      filter: 'select',
      width: 140,
      renderer: { classNames: ['text_secondary'] },
    },
  ];

  useEffect(() => {
    setColumns([...prefixColumns, ...(type === 's' ? expireColumns : []), ...suffixColumns]);
  }, [i18n.language]);

  useEffect(() => {
    if (size.innerSize.W > 1024) handlSearch();
  }, [userInputDateRange, useYn[COMCD_USE_YN], debouncedTitle, initNoticeList]);

  useEffect(() => {
    if (pickerDateRange[0].startDate) {
      setUserInputDateRange((prev) => ({ ...prev, start: dayjs(pickerDateRange[0].startDate).format('YYYY-MM-DD') }));
    }
    if (pickerDateRange[0].endDate) {
      setUserInputDateRange((prev) => ({ ...prev, end: dayjs(pickerDateRange[0].endDate).format('YYYY-MM-DD') }));
    }
  }, [pickerDateRange[0].startDate, pickerDateRange[0].endDate]);

  useEffect(() => {
    if (cDate !== '') setRowState((prev: any) => ({ ...prev, cDate: cDate?.replace('-', '') }));
  }, [cDate, selectedRowKey]);

  const handlSearch = (filterRef?: RefObject<HTMLDivElement>) => {
    if (filterRef) {
      // 모바일일 경우 검색 필터 모달 창 닫기
      filterRef.current?.classList.remove('active');
      setOpenModal({ status: false, type: 'filter' });
    }
    if (initNoticeList) applyFilter(initNoticeList);
  };

  const onEnterKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handlSearch();
    }
  };

  const convertToDate = (dateString: string) => new Date(`${dateString}T00:00:00`);

  const applyFilter = (array: any[]) => {
    const titleFilteredArray = array.filter((item: any) => {
      return item.cTitle?.toLowerCase()?.includes(searchTitle.title?.toLowerCase());
    });
    let newFilter = titleFilteredArray;
    if (userInputDateRange.end !== '') {
      newFilter = titleFilteredArray.filter((item) => {
        const itemDate = convertToDate(item.cDate);
        const startDate = convertToDate(userInputDateRange.start);
        const endDate = convertToDate(userInputDateRange.end);

        if (userInputDateRange.start === '') return itemDate <= endDate;
        return itemDate >= startDate && itemDate <= endDate;
      });
    }

    const result = ynFilter(newFilter, 'useYn', useYn[COMCD_USE_YN]);
    if (result.length > 0) {
      const newArray = result.map((el: any, i: number) => ({ ...el, rowKey: i, sortKey: i }));
      setNoticeListForTui(newArray);
    } else {
      setNoticeListForTui([]);
    }
  };

  const {
    data: noticeList,
    isLoading: isLoadingNoticeList,
    isFetching,
    isRefetching,
    isError,
    refetch: refetchNoticeList,
  } = useQuery(['noticeList', userInfo.hCd, userInfo.sCd], () => fetchNoticeList(method), {
    enabled: !!userInfo.hCd && !!userInfo.sCd,
    cacheTime: 0,
  });

  const getParams = (methodParam: string) => {
    const commonReq = { ...reqInfo.reqParam };
    if (methodParam === 'get') return commonReq;
    return { ...commonReq, cSeq, ...(type === 's' && { editor: userInfo.userId }) };
  };

  const fetchNoticeList = async (methodParam: string) => {
    try {
      const req = getParams(methodParam);
      let res;
      let list = [];
      if (methodParam === 'get') {
        res = await apiGet({ path: reqInfo.path, req });
      }

      if (type === 's') list = res.data?.data?.communicateList;
      else list = res.data.data;

      if (list) return { method, data: list };
      return { method };
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  useEffect(() => {
    // 조회/삭제 성공시
    if (noticeList) {
      // 조회했을 때
      if (noticeList.method === 'get') {
        setNoticeListForTui(noticeList.data);
        setInitNoticeList(noticeList.data);
      } else {
        // 삭제했을 때
        setMethod('get'); // 데이터 리패치

        setSelectedRowKey('');
        initiateState();
        logPost({
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          userId: userInfo.userId,
          menu: '현장 전달사항',
          action: '삭제',
          etc: `${rowState.cSeq}`,
        });
      }
    }
  }, [noticeList]);

  const { data: noticeData, isError: isErrorNoticeData } = useQuery(['noticeGet', userInfo.hCd, userInfo.sCd, cSeq], () => fetchNoticeData(), {
    enabled: !!userInfo.hCd && !!userInfo.sCd && cSeq !== null && type === 's',
    cacheTime: 0,
  });

  const fetchNoticeData = async () => {
    try {
      const req = { hCd: userInfo.hCd, sCd: userInfo.sCd, cSeq };
      const res = await apiGet({ path: '/safe/communicateview', req });
      return res.data.data;
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  useEffect(() => {
    if (noticeData) {
      setRowState(noticeData);
      setIsNewAdd(false);
      setEditorData(noticeData[contentKey]);
    }
  }, [noticeData]);

  const createNewNoticeAPI = async (reqData: any) => {
    const req = trimObject(reqData);
    const res = await apiPost({ path: reqInfo.path, contentType: 'application/json', req });
    const { statusCode, data, message } = res.data;
    if (statusCode === 200) {
      if (type === 's') {
        setRowState(data);
        setEditorData(data[contentKey]);
        setInitNoticeList((prev) => [data, ...prev]);
        setCSeq(data.cSeq);
      }
      if (type === 'c') {
        setEditorData(reqData[contentKey]);
        setIsNewAdd(false);
        setInitNoticeList(data);

        // 추가된 객체 찾기
        const addedObject = data.filter((item2: any) => !initNoticeList.some((item1: any) => item1.cSeq === item2.cSeq));
        if (addedObject[0]) setRowState(addedObject[0]);
      }

      setSelectedRowKey('');
      toast.success(t(message));
      setIsNewAdd(false);
      await logPost({
        hCd: userInfo.hCd,
        sCd: userInfo.sCd,
        userId: userInfo.userId,
        menu: '안전관리/게시판-현장 전달사항',
        action: '신규등록',
        etc: `${data.cSeq}`,
      });
    } else {
      console.log(statusCode, message);
    }
  };

  const updateNoticeAPI = async (reqData: any) => {
    const req = trimObject(reqData);
    const res = await apiPost({ path: reqInfo.path, contentType: 'application/json', req });
    const { statusCode, data, message } = res.data;

    if (statusCode === 200) {
      if (type === 's') {
        setRowState(data);
        setEditorData(data[contentKey]);

        // 수정 데이터 tui에서 찾아서 수정
        const copyInitArray = [...initNoticeList];
        const findInitIndex = initNoticeList.findIndex((el: any) => el.cSeq === data.cSeq);

        if (findInitIndex !== -1) {
          copyInitArray[findInitIndex] = data;
          setInitNoticeList(copyInitArray);
        }
      }

      if (type === 'c') {
        setEditorData(reqData[contentKey]);
        setIsNewAdd(false);
        setInitNoticeList(data);

        const updateObj = data.find((el: any) => el.cSeq === reqData.cSeq);
        setRowState(updateObj);
      }

      toast.success(t(message));
      await logPost({
        hCd: userInfo.hCd,
        sCd: userInfo.sCd,
        userId: userInfo.userId,
        menu: '안전관리/게시판-현장 전달사항',
        action: '저장',
        etc: `${data.cSeq}`,
      });
    } else if (statusCode === 400) {
      const test = message.find((el: string) => el.includes('cContent must be shorter than'));
      if (test) toast.warn(t('내용 입력 글자 수 초과입니다.'));
    }
  };

  const onClickInitiateSearchOption = () => {
    setSearchTitle({ title: '' });
    setUserInputDateRange({ start: '', end: '' });
    setPickerDateRange([{ startDate: '', endDate: '', key: 'selection' }]);
    setUseYn(INIT_USE_YN_A);
    onClickNewRegistration(); // 우측 인풋창도 초기화
  };

  // 사용자 Row 클릭
  const onClickRow = (rowData: any) => {
    if (rowData.cSeq !== '') {
      setCDate(rowData?.cDate?.replaceAll('-', ''));
      setDate({ start: rowData?.cDate || '', end: rowData?.fDate || '' });
      setCSeq(rowData.cSeq.toString());

      if (type === 'c') {
        setRowState(rowData);
        setEditorData(rowData[contentKey]);
        setIsNewAdd(false);
      }
    }
    setSelectedRowKey(rowData.rowKey);
    if (size.innerSize.W <= 1024) {
      setOpenSubPage(true);
    }
  };

  // 인풋폼 초기화
  const initiateState = () => {
    Object.keys(rowState).map((el: any) => {
      // 기본값 제외하고 초기화
      return setRowState((prev: any) => ({
        ...prev,
        [el]: '',
        useYn: 'Y',
        writer: userInfo.userId,
        editor: userInfo.userId,
      }));
    });
    setIsNewAdd(true);
    setIsSaveClicked(false);
    setCDate('');
    setDate({ start: '', end: '' });
    setEditorData('');
    setCSeq(null);
  };

  const onClickNewRegistration = () => {
    initiateState();
    setSelectedRowKey('');
    setOpenSubPage(true);
  };

  const onClickSave = async () => {
    setIsSaveClicked(true);
    const { bigo, cTitle } = rowState;
    const newData: any = {
      cSeq: rowState.cSeq,
      cTitle,
      bigo,
      // cDate: cDate || '',
      [contentKey]: editorData,
      useYn: rowState.useYn,
      ...(type === 's' && {
        hCd: userInfo.hCd,
        sCd: userInfo.sCd,
        editor: userInfo.userId,
        writer: isNewAdd ? userInfo.userId : rowState.writer,
        cDate: date.start?.replaceAll('-', '') || '',
        fDate: date.end?.replaceAll('-', '') || '',
      }),
      ...(type === 'c' && { cDate: cDate || '', cCd: userInfo.hCd }),
    };
    const { wDate, eDate, ...resultData } = newData;
    if (newData.cTitle?.trim() === '') return toast.warning(t('제목을 입력하세요'));
    if (type === 's') {
      if (!date.start?.replaceAll('-', '') || !date.end?.replaceAll('-', '')) return toast.warning(t('게시 기간을 선택하세요'));
      if (date.start?.replaceAll('-', '') && (date.start?.replaceAll('-', '').length < 8 || !isValidYYYYMMDD(date.start?.replaceAll('-', '')))) return toast.warning(t('게시 기간을 확인하세요'));
      if (date.end?.replaceAll('-', '') && (date.end?.replaceAll('-', '').length < 8 || !isValidYYYYMMDD(date.end?.replaceAll('-', '')))) return toast.warning(t('게시 기간을 확인하세요'));
    } else {
      if (!cDate) return toast.warning(t('적용일자를 선택하세요'));
      if (cDate && (cDate.length < 8 || !isValidYYYYMMDD(cDate))) return toast.warning(t('적용일자를 확인하세요'));
    }
    if (isNewAdd) return createNewNoticeAPI(resultData);
    return updateNoticeAPI(resultData);
  };

  useEffect(() => {
    refetchNoticeList();
  }, [method]);

  const deleteAPI = async () => {
    try {
      const req = { ...reqInfo.reqParam, cSeq, ...(type === 's' && { editor: userInfo.userId }) };
      const res = await apiDelete({ path: reqInfo.path, req });
      if (res.data.statusCode === 200) {
        toast.success(t(res.data.message));
        onClickNewRegistration(); // 모두 초기화

        if (type === 'c') setInitNoticeList(res.data.data);
        else refetchNoticeList();
        await logPost({
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          userId: userInfo.userId,
          menu: '안전관리 > 현장 전달사항',
          action: '현장 전달사항 상세 삭제',
          etc: `현장 전달사항 제목 : ${rowState.cTitle}`,
        });
      }
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  const onClickDelete = () => {
    setOpenModal((prev) => ({ ...prev, status: true, type: 'delete', api: deleteAPI }));
  };

  const onClickRangeInput = () => {
    if (!visibleRangePicker) setVisibleRangePicker(true);
  };

  const onClickDashboardView = () => {
    const newData: any = {
      ...rowState,
      cDate: formatDateYMD(cDate),
      cContent: editorData,
      widgetSize: type === 's' ? 'small' : 'large',
    };

    setOpenModal((prev) => ({ ...prev, status: true, type: 'dashboardView', data: newData }));
  };

  const backToMain = () => {
    initiateState();
    setSelectedRowKey('');
    setOpenSubPage(false);
  };

  const { onBackPrev } = useBackHistory({ isOpen: openSubPage, onPrev: backToMain, pageName: 'transferPage' });

  // 필터 버튼 클릭
  const onClickFilter = () => {
    setOpenModal({ status: true, type: 'filter' });
  };

  const hasDataForTab = noticeList && !!noticeList.data;
  const isLoading = !columns || isFetching || isRefetching || isLoadingNoticeList;
  const shouldRenderData = !isLoading && hasDataForTab && noticeListForTui;

  if (isError) return <IssueGuide />;
  if (isLoading) return null;

  return (
    <div className={`content-container ${size.innerSize.W > 1024 ? 'twoColumn column-55 max800' : 'oneColumn'}`}>
      <Root loading={isLoading} className={size.innerSize.W > 1024 || (size.innerSize.W <= 1024 && !openSubPage) ? 'showRoot' : 'hideRoot'}>
        <SearchOptions align='left'>
          {size.innerSize.W > 1024 ? (
            <>
              <div className='inputsWrapper'>
                <div className='inputForm-row'>
                  <div className='inputForm-col withLabelComCf'>
                    <label htmlFor='useYn'>{t(type === 's' ? `사용유무` : `상황판 적용`)}</label>
                    <SelectBox
                      options={useYnComCdListWithAll}
                      defaultOption={useYn.cdName}
                      state={useYn}
                      setState={setUseYn}
                      stateKey={COMCD_USE_YN}
                      initiateKey={useYn[COMCD_USE_YN]}
                      filterbar='filter-1-left'
                    />
                  </div>
                </div>

                <div className='inputForm-row-fit'>
                  <span className='calendarLabel'>{type === 's' ? t('게시 기간') : t('적용일자')}</span>
                  <div className='inputForm-col'>
                    <div className='flex-basic'>
                      <input id='startInput' type='text' value={userInputDateRange.start} onChange={(e) => onChangeRangeInput(e, 'start', setPickerDateRange, setUserInputDateRange)} />
                      <span className='wave'> ~ </span>
                      <input id='endInput' type='text' value={userInputDateRange.end} onChange={(e) => onChangeRangeInput(e, 'end', setPickerDateRange, setUserInputDateRange)} />
                    </div>
                    {visibleRangePicker && (
                      <div className='rangePickerWrapper'>
                        <RangePicker state={pickerDateRange} setState={setPickerDateRange} setVisible={setVisibleRangePicker} />
                      </div>
                    )}
                  </div>
                  <button type='button' className='date-picker-btn' onClick={onClickRangeInput}>
                    <span className='material-symbols-rounded'>calendar_month</span>
                  </button>
                </div>
                <div className='inputForm-row'>
                  <div className='inputForm-col'>
                    <Input label='' placeholder={t('제목')} type='text' id='title' name='title' state={searchTitle} setState={setSearchTitle} />
                  </div>
                </div>
              </div>
              <div className='inputsWrapper'>
                <div className='secondSearchOption'>
                  <div className='flex-basic textBtnGroup'>
                    <BtnGhost onClick={onClickInitiateSearchOption}>{t('초기화')}</BtnGhost>
                    <div className='searchResult'>
                      {t('총')}
                      <span>{noticeListForTui?.length}</span>
                      {t('개')}
                    </div>
                  </div>
                  <div className='flex-basic iconBtnGroup'>
                    <BtnGhost onClick={onClickNewRegistration}>
                      <span className='material-symbols-rounded'>add</span>
                      {t('등록')}
                    </BtnGhost>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className='filter-wrapper'>
              <div className='inputsWrapper'>
                <div className='inputForm-row' style={{ width: 'calc(100% - 3.5rem)' }}>
                  <div className='inputForm-col'>
                    <Input label='' placeholder={t('제목')} type='search' id='title' name='title' state={searchTitle} setState={setSearchTitle} onKeyDown={onEnterKeyDown} ico='search' />
                  </div>
                </div>
                <BtnGhost onClick={onClickFilter} className='filter-btn'>
                  <span className='material-symbols-rounded'>filter_alt</span>
                </BtnGhost>
              </div>
              <div className='inputsWrapper'>
                <div className='flex-basic iconBtnGroup'>
                  <BtnGhost onClick={onClickNewRegistration}>
                    <span className='material-symbols-rounded'>add</span>
                    {t('등록')}
                  </BtnGhost>
                </div>
              </div>
            </div>
          )}
        </SearchOptions>
        {isLoading ? (
          <LoadingModalBackground>
            <PulseLoader className='flex-center' color='rgb(0, 122, 255)' size='1rem' style={{ width: 'inherit', height: 'inherit' }} />
          </LoadingModalBackground>
        ) : shouldRenderData ? (
          <TuiGridWrapper componentRef={componentRef}>
            <TuiGrid data={noticeListForTui} columns={columns} onClickRow={onClickRow} perPage={15} rowKey={selectedRowKey} scrollX height={tuiHeight} frozenCount={1} usePagenation idxKey='cSeq' />
          </TuiGridWrapper>
        ) : (
          <IssueGuide />
        )}
      </Root>
      {(size.innerSize.W > 1024 || (size.innerSize.W <= 1024 && openSubPage)) && (
        <Root loading={false}>
          <div className='inputFormsWrapper'>
            {size.innerSize.W <= 1024 && <BackButton func={onBackPrev} />}
            <div className='formTitle'>{isNewAdd ? `${t('새로운')} ${type === 's' ? t(`전달사항`) : t(`공지사항`)}` : `${type === 's' ? t(`전달사항`) : t(`긴급 공지사항`)} ${t('상세')}`}</div>
          </div>
          {!isErrorNoticeData ? (
            <SiteTransferEditorInputForm
              type={type}
              contentKey={contentKey}
              state={rowState}
              setState={setRowState}
              isNewAdd={isNewAdd}
              userInfoInputFormRef={userInfoInputFormRef}
              onClickNewAdd={onClickNewRegistration}
              onClickSave={onClickSave}
              onClickDelete={onClickDelete}
              onClickPreview={onClickDashboardView}
              isSaveClicked={isSaveClicked}
              date={date}
              setDate={setDate}
              cDate={cDate}
              setCDate={setCDate}
              editorData={editorData}
              setEditorData={setEditorData}
            />
          ) : (
            <IssueGuide />
          )}
        </Root>
      )}
      <Portal openModal={openModal?.status}>
        {openModal && openModal.type === 'delete' && <DeleteModal openModal={openModal} setOpenModal={setOpenModal} />}
        {openModal && openModal.type === 'dashboardView' && <DashboardViewModal openModal={openModal} setOpenModal={setOpenModal} />}
      </Portal>
      {size.innerSize.W <= 1024 && (
        <Portal openModal>
          <SearchFilterModal openModal={openModal} setOpenModal={setOpenModal} onClickSearch={handlSearch} onClickReset={onClickInitiateSearchOption}>
            <div className='filter-option-wrapper'>
              <div className='filter-option-col filter-underline filter-option-date'>
                <span className='calendarLabel'>{type === 's' ? t('게시 기간') : t('적용일자')}</span>
                <div className='inputForm-col'>
                  <div className='flex-basic' role='presentation' onClick={onClickRangeInput}>
                    <input id='startInput' type='text' readOnly value={userInputDateRange.start} onChange={(e) => onChangeRangeInput(e, 'start', setPickerDateRange, setUserInputDateRange)} />
                    <span className='wave'> ~ </span>
                    <input id='endInput' type='text' readOnly value={userInputDateRange.end} onChange={(e) => onChangeRangeInput(e, 'end', setPickerDateRange, setUserInputDateRange)} />
                  </div>
                  {visibleRangePicker && (
                    <div className='rangePickerWrapper'>
                      <RangePicker state={pickerDateRange} setState={setPickerDateRange} setVisible={setVisibleRangePicker} />
                    </div>
                  )}
                </div>
              </div>
              <div className='filter-option-cont'>
                <div className='filter-option-row'>
                  <label htmlFor='useYn'>{t(type === 's' ? `사용유무` : `상황판 적용`)}</label>
                  <SelectBox
                    options={useYnComCdListWithAll}
                    defaultOption={useYn.cdName}
                    state={useYn}
                    setState={setUseYn}
                    stateKey={COMCD_USE_YN}
                    initiateKey={useYn[COMCD_USE_YN]}
                    filterbar='filter-1-left'
                  />
                </div>
              </div>
            </div>
          </SearchFilterModal>
        </Portal>
      )}
    </div>
  );
};

export default Transfer;
